import { calculateProjection, type ProjectionMovement } from '@financego/core';
import { Check, CircleDashed, X } from 'lucide-react';
import { useMemo } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { useClearAgenda, useSetAgenda } from '../transactions/transactions.api';
import { formatLongDate, formatMoney } from './dashboard-format';
import type { DashboardSnapshot } from './dashboard.types';

interface AgendaOccurrence extends ProjectionMovement {
  readonly status: 'done' | 'not-done' | 'pending';
}

export function AgendaView({ snapshot }: { readonly snapshot: DashboardSnapshot }) {
  const { locale, t } = useI18n();
  const setAgenda = useSetAgenda();
  const clearAgenda = useClearAgenda();
  const occurrences = useMemo(() => buildAgendaOccurrences(snapshot), [snapshot]);
  const grouped = useMemo(() => groupOccurrences(occurrences), [occurrences]);
  const isPending = setAgenda.isPending || clearAgenda.isPending;

  const updateStatus = async (occurrence: AgendaOccurrence, status: AgendaOccurrence['status']) => {
    if (occurrence.transactionId === null) return;
    if (status === 'pending') {
      await clearAgenda.mutateAsync({
        id: occurrence.transactionId,
        occurrenceDate: occurrence.date,
      });
      return;
    }
    await setAgenda.mutateAsync({
      id: occurrence.transactionId,
      occurrenceDate: occurrence.date,
      status,
    });
  };

  return (
    <>
      <header className="dashboard-heading agenda-heading">
        <p className="eyebrow">{t('navigation.agenda')}</p>
        <h1>{t('agenda.title')}</h1>
        <p>{t('agenda.description')}</p>
      </header>

      {grouped.length === 0 ? (
        <section className="agenda-empty">
          <Check aria-hidden="true" />
          <p>{t('agenda.empty')}</p>
        </section>
      ) : (
        <div className="agenda-groups">
          {grouped.map(([date, movements]) => (
            <section className="agenda-day" key={date} aria-labelledby={`agenda-${date}`}>
              <h2 id={`agenda-${date}`}>{formatLongDate(date, locale)}</h2>
              <div className="agenda-occurrences">
                {movements.map((movement, index) => (
                  <article
                    className={`agenda-occurrence is-${movement.status} movement-${movement.type}`}
                    key={`${movement.transactionId ?? movement.label}-${String(index)}`}
                  >
                    <div className="agenda-occurrence-main">
                      <span className={`agenda-type-dot is-${movement.type}`} aria-hidden="true" />
                      <div>
                        <strong>{movement.label}</strong>
                        <small>
                          {t(
                            movement.status === 'not-done'
                              ? 'agenda.projectionExcluded'
                              : 'agenda.projectionIncluded',
                          )}
                        </small>
                      </div>
                      <b>
                        {movement.type === 'income' ? '+' : movement.type === 'expense' ? '−' : ''}
                        {formatMoney(movement.amount, locale)}
                      </b>
                    </div>
                    <div className="agenda-status-actions" aria-label={movement.label}>
                      <button
                        aria-pressed={movement.status === 'done'}
                        disabled={isPending}
                        type="button"
                        onClick={() => void updateStatus(movement, 'done')}
                      >
                        <Check aria-hidden="true" /> {t('agenda.done')}
                      </button>
                      <button
                        aria-pressed={movement.status === 'not-done'}
                        disabled={isPending}
                        type="button"
                        onClick={() => void updateStatus(movement, 'not-done')}
                      >
                        <X aria-hidden="true" /> {t('agenda.notDone')}
                      </button>
                      {movement.status !== 'pending' && (
                        <button
                          disabled={isPending}
                          type="button"
                          onClick={() => void updateStatus(movement, 'pending')}
                        >
                          <CircleDashed aria-hidden="true" /> {t('agenda.pending')}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function buildAgendaOccurrences(snapshot: DashboardSnapshot): readonly AgendaOccurrence[] {
  const validationMap = new Map(
    snapshot.agenda.map((validation) => [
      `${validation.transactionId}:${validation.occurrenceDate}`,
      validation.status,
    ]),
  );
  return calculateProjection(snapshot.coreTransactions, {
    ignoreValidation: true,
    monthsAhead: 3,
    today: snapshot.today,
  })
    .points.flatMap((point) =>
      point.movements.map((movement) => ({
        ...movement,
        status:
          movement.transactionId === null
            ? ('pending' as const)
            : (validationMap.get(`${movement.transactionId}:${movement.date}`) ?? 'pending'),
      })),
    )
    .slice(0, 80);
}

function groupOccurrences(occurrences: readonly AgendaOccurrence[]) {
  const groups = new Map<string, AgendaOccurrence[]>();
  for (const occurrence of occurrences) {
    const group = groups.get(occurrence.date) ?? [];
    group.push(occurrence);
    groups.set(occurrence.date, group);
  }
  return [...groups.entries()];
}
